import { Array, identity, Number, pipe, Record } from 'effect'
import { z } from 'zod'

import { CORE_CONSTANT } from '../../../../constant.ts'
import type {
  ViewQueryDimensionFieldForMultiTablePivot,
  ViewQueryMultiTablePivot,
  ViewQueryMultiTablePivotQuery,
} from '../../../../schemas/query/index.ts'
import type { ViewUiDimensionCandidate, ViewUiDimensionCandidateProperty } from '../../../../schemas/ui/dimension.ts'
import type { ViewUiKpiPivot } from '../../../../schemas/ui/kpiPivot.ts'
import type {
  ViewConfigAdditionalConfig,
  ViewConfigDimension,
  ViewConfigKpiPivot,
  ViewConfigKpiWithMetric,
  ViewConfigPivot,
} from '../../../../schemas/view_config/index.ts'
import type { CompileContext } from '../../common.ts'
import { getMetricFromKpiFormula } from '../../getMetricFromKpiFormula.ts'
import { hasSheetPerformanceWarnings } from '../../hasPerformanceWarnings.ts'
import { compileKpiViewConfigWithoutGoal } from '../kpi/compileKpiViewConfig.ts'
import { generateNodeFromDimension } from '../kpi/generateNodeFromDimension.ts'
import { generateDimensionFieldName } from '../sheet/measure/generateDimensionField.ts'
import { isMatchToDimensionGroup } from '../sheet/searchFieldAndPropertyForDimension.ts'
import { compileGoalConfig } from './compileGoalConfig.ts'

export interface CompiledKpiPivotViewConfig {
  type: 'kpiPivot'
  queries: [ViewQueryMultiTablePivot]
  query: ViewQueryMultiTablePivot
  ui: [ViewUiKpiPivot]
  context: CompileContext
}

// eslint-disable-next-line sonarjs/todo-tag
// TODO: KpiPivotSheetとしてcompileしてしまっているので、KpiSheetにしたい。

export function compileKpiPivotViewConfig(
  config: ViewConfigKpiPivot,
  context: CompileContext,
): CompiledKpiPivotViewConfig {
  const { logs, viewSearcher } = context
  const pivot: ViewConfigPivot | undefined =
    context.additionalConfig?.kpiParameter?.pivot ?? CORE_CONSTANT.KPI_PIVOT_DEFAULT_PRESET().parameter.pivot
  // SQL上でkpiは不要なので、pivotからkpiの軸を外したくなるが、その操作を行うと諸々の整合性が取りづらくなるのでここで除去せず、measure側でよしなに除去する
  // const pivotWithoutKpi = pivot !== undefined ? {
  //   rows: pivot.rows.filter(x => x.fieldName !== CORE_CONSTANT.KPI_PIVOT_KPI_DIMENSION.fieldName),
  //   columns: pivot.columns.filter(x => x.fieldName !== CORE_CONSTANT.KPI_PIVOT_KPI_DIMENSION.fieldName),
  // } : undefined
  const kpis = pipe(
    config.kpis,
    Array.filter((kpiReference) => {
      // 本当はここのコンパイルは実行し、UIレイヤでKPIを非表示にしたいが、ピボットの列情報はqueryの実行時に動的に決まるため、ここで排除する以外に方法がない
      if (isInvisibleKpi(kpiReference.viewId, context.additionalConfig)) {
        return false
      }
      if (
        isFilteredKpiGroup(kpiReference.kpiGroupName ?? CORE_CONSTANT.KPI_GROUP_DEFAULT_NAME, context.additionalConfig)
      ) {
        return false
      }
      return true
    }),
    Array.flatMapNullishOr((kpiReference) => {
      const view = viewSearcher.searchView(kpiReference.viewId)
      if (view === undefined) {
        // eslint-disable-next-line sonarjs/todo-tag
        // TODO: 削除側の実装がやや微妙でエラーになりやすいので、一旦エラーを表示しないようにした。
        // logs.error(
        //   `KPIビューが見つかりませんでした。viewId: ${kpiRef.viewId}. 現在のビュー: ${viewSearcher.views
        //     .map((x) => x.id)
        //     .join(',')}`,
        // )
        return
      }
      const kpi = view.config
      if (kpi.type !== 'kpi') {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        logs.error(`このビューはKPIではありません。viewId: ${kpiReference.viewId}, type: ${kpi.type ?? ''}`)
        return
      }
      return {
        kpiRef: kpiReference,
        view,
        kpi,
      }
    }),
  )

  const compiles = pipe(
    kpis,
    Array.flatMapNullishOr(({ kpiRef, view, kpi }) => {
      const metric = getMetricFromKpiFormula(kpi.formula)
      if (metric === undefined) {
        logs.warn(`KPI設定が完了していません`)
      }
      const compiled =
        metric === undefined
          ? undefined
          : compileKpiViewConfigWithoutGoal(metric, pivot, {
              ...context,
              resources: {
                ...context.resources,
                view,
              },
            })
      const query: ViewQueryMultiTablePivotQuery = {
        // eslint-disable-next-line sonarjs/todo-tag
        name: [view.id, `actual`].join('_'), // TODO: 結局使っていない
        kpiId: view.id,
        kpiName: view.name,
        kpiGroupName: kpiRef.kpiGroupName ?? CORE_CONSTANT.KPI_GROUP_DEFAULT_NAME,
        role: `actual`,
        schema: compiled?.query ?? null,
      }
      if (metric !== undefined && hasSheetPerformanceWarnings(metric.sheet, context.modelSearcher)) {
        logs.warn(`KPI「${view.name}」で表示に時間のかかる条件を使用しています。KPI設定から条件の見直しを推奨します`)
      }
      return {
        compiled,
        query,
      }
    }),
  )
  const sortKeys = context.additionalConfig?.kpiParameter?.sorter?.columnKeys ?? []
  const query: ViewQueryMultiTablePivot = {
    type: 'multiTablePivot',
    tree:
      sortKeys.length > 0
        ? generateNodeFromDimension({ rows: [], columns: [] }, 0, [])
        : generateNodeFromDimension(pivot, 0, []),
    pivot: {
      rows: generateQueryPivotDimensions(
        pivot.rows,
        kpis.map((x) => x.view.id),
      ),
      columns: generateQueryPivotDimensions(
        pivot.columns,
        kpis.map((x) => x.view.id),
      ),
    },
    sorter: {
      ...context.additionalConfig?.kpiParameter?.sorter,
      totalRowOrder: context.additionalConfig?.kpiParameterPerUser?.totalRowOrder,
      totalColumnOrder: context.additionalConfig?.kpiParameterPerUser?.totalColumnOrder,
    },
    queries: Array.flatMapNullishOr(
      [
        ...compiles.map((x) => x.query),
        ...compileGoalConfig({
          goalConfig: context.additionalConfig?.kpiParameter?.goalConfig,
          pivot,
          context,
          kpiIds: kpis.map((x) => x.view.id), // goalConfigに紐づくkpis = このコンテキストのkpis が常に成り立つだろうか？
        }),
      ],
      identity,
    ),
    options: {
      skipTotal: context.additionalConfig?.kpiParameterPerUser?.skipTotal,
    },
  }

  const ui: ViewUiKpiPivot = {
    type: 'KpiPivotSheet',
    kpis: kpis.map((kpi) => ({
      id: kpi.view.id,
      label: kpi.view.name,
      withDateField: getMetricFromKpiFormula(kpi.kpi.formula)?.date !== undefined,
      groupName: kpi.kpiRef.kpiGroupName,
      ...kpi.kpi.ui?.kpi,
    })),
    dimensions: generateDimensionsCandidatesForMultiKpi(
      [...compiles.flatMap((x) => x.compiled?.ui.dimensions ?? []), CORE_CONSTANT.KPI_PIVOT_KPI_DIMENSION],
      kpis.map((x) => x.kpi),
      context,
    ),
    pivot: {
      // eslint-disable-next-line sonarjs/todo-tag
      // TODO: この愚直なマージで方法で、各種ケースを網羅できてるか不安
      rows: pivot.rows.map((row, index) => {
        const uiPivotRows = Array.flatMapNullishOr(
          compiles.map((x) => x.compiled?.ui.pivot.rows[index]),
          identity,
        )
        return {
          key: row.key,
          label:
            Array.flatMapNullishOr(
              uiPivotRows.map((x) => x.label),
              identity,
            )[0] ?? row.label,
          fieldType:
            Array.flatMapNullishOr(
              uiPivotRows.map((x) => x.fieldType),
              identity,
            )[0] ?? `string`,
          fieldMetaType: Array.flatMapNullishOr(
            uiPivotRows.map((x) => x.fieldMetaType),
            identity,
          )[0],
          selectOptions: Array.flatMapNullishOr(
            uiPivotRows.map((x) => x.selectOptions),
            identity,
          )[0],
        }
      }),
      columns: pivot.columns.map((column, index) => {
        const uiPivotColumns = Array.flatMapNullishOr(
          compiles.map((x) => x.compiled?.ui.pivot.columns[index]),
          identity,
        )
        return {
          key: column.key,
          label:
            Array.flatMapNullishOr(
              uiPivotColumns.map((x) => x.label),
              identity,
            )[0] ?? column.label,
          fieldType:
            Array.flatMapNullishOr(
              uiPivotColumns.map((x) => x.fieldType),
              identity,
            )[0] ?? `string`,
          fieldMetaType: Array.flatMapNullishOr(
            uiPivotColumns.map((x) => x.fieldMetaType),
            identity,
          )[0],
          selectOptions: Array.flatMapNullishOr(
            uiPivotColumns.map((x) => x.selectOptions),
            identity,
          )[0],
        }
      }),
    },
    appearance: config.ui?.appearance,
  }
  return {
    type: 'kpiPivot',
    ui: [ui],
    query,
    queries: [query],
    context,
  }
}

function generateDimensionsCandidatesForMultiKpi(
  ds: ViewUiDimensionCandidate[],
  kpis: ViewConfigKpiWithMetric[],
  context: CompileContext,
): ViewUiDimensionCandidate[] {
  // const fieldTypeDimensions: ViewUiDimensionCandidate[] = ds
  //   .map((x) => (x.type === 'fieldName' ? x : undefined))
  //   .compact()
  //   .groupBy((x) => x.key)
  //   .transformValues((dimensions): ViewUiDimensionCandidate => {
  //     const dimension = dimensions.first()!
  //     return {
  //       ...dimension,
  //       dimensionCount: dimensions.length,
  //     }
  //   })
  //   .values()

  const propertyTypeDimensions: ViewUiDimensionCandidateProperty[] = pipe(
    ds,
    Array.flatMapNullishOr((x) => (x.type === 'property' ? x : undefined)),
    Array.groupBy((x) => x.key),
    Record.map((dimensions) => {
      const [dimension] = dimensions
      return {
        ...dimension,
        dimensionCount: dimensions.length,
      }
    }),
    Record.values,
  )

  const { dimensionGroups } = context.resources.organizationSetting
  const goalConfig = context.additionalConfig?.kpiParameter?.goalConfig
  const dimensionGroupTypeDimensions = Array.flatMapNullishOr(
    dimensionGroups.map((group): ViewUiDimensionCandidate | undefined => {
      const matched = Array.flatMapNullishOr(
        group.properties.map((dimensionGroupProperty) =>
          propertyTypeDimensions.find((x) =>
            isMatchToDimensionGroup(
              x.property,
              dimensionGroupProperty,
              context.additionalConfig?.kpiParameter?.goalConfig,
            ),
          ),
        ),
        identity,
      )
      const matchedToGoal =
        Array.intersection(
          Array.flatMapNullishOr(
            [
              goalConfig?.goalDimension1?.id,
              goalConfig?.goalDimension2?.id,
              goalConfig?.goalDimension3?.id,
              goalConfig?.goalDimension4?.id,
              goalConfig?.goalDimension5?.id,
            ],
            identity,
          ),
          Array.flatMapNullishOr(
            group.properties.map((x) => (x.type === 'goalDimension' ? x.goalDimensionId : undefined)),
            identity,
          ),
        ).length > 0
      // eslint-disable-next-line sonarjs/no-dead-store,sonarjs/no-unused-vars
      const matches = matched.length + (matchedToGoal ? 1 : 0)
      // 初期仕様だと、一致数が2未満だと出す意味がないとみなして表示していなかったが、ユーザーにとって分かりづらい仕様なのでやめる
      // if (matches < 2) {
      //   return undefined
      // }
      return {
        type: 'dimensionGroup',
        key: [`g`, group.id].join('_'),
        label: group.label,
        groupId: group.id,
        // eslint-disable-next-line sonarjs/todo-tag
        fieldType: 'string', // TODO: どうしよう？
        dimensionCount: Number.sumAll(matched.map((x) => x.dimensionCount ?? 0)),
      }
    }),
    identity,
  )
  // const labelTypeDimensions = ds
  //   .uniqueBy((x) => x.label)
  //   .map(
  //     (x): ViewUiDimensionCandidate => ({
  //       ...x,
  //       type: 'fieldLabel',
  //       fieldName: x.label,
  //     }),
  //   )
  // const aliases = kpis.flatMap((x) => x.measures).flatMap((x) => x.measureWithDimension.dimensionAliases ?? [])
  // const aliasTypeDimensions = aliases
  //   .uniqueBy((x) => x.alias)
  //   .map(
  //     (x): ViewUiDimensionCandidate => ({
  //       type: 'alias',
  //       key: `a_${x.alias}`,
  //       alias: x.alias,
  //       fieldType: 'string',
  //       label: x.alias,
  //     }),
  //   )
  const dateKpis = Array.flatMapNullishOr(
    kpis.map((x) => (getMetricFromKpiFormula(x.formula)?.date === undefined ? undefined : x)),
    identity,
  )

  return pipe(
    [
      {
        ...CORE_CONSTANT.KPI_PIVOT_KPI_GROUP_DIMENSION(),
        dimensionCount: kpis.length,
      },
      {
        ...CORE_CONSTANT.KPI_PIVOT_KPI_DIMENSION,
        dimensionCount: kpis.length,
      },
      {
        ...CORE_CONSTANT.KPI_PIVOT_KPI_DATE_DIMENSION(),
        dimensionCount: dateKpis.length,
      },
      // ...fieldTypeDimensions,
      ...propertyTypeDimensions,
      ...dimensionGroupTypeDimensions,
      // ...labelTypeDimensions,
      // ...aliasTypeDimensions,
    ],
    Array.filter((x) => x.dimensionCount === undefined || x.dimensionCount > 0),
    Array.dedupeWith((a, b) => a.key === b.key),
  )
}

function generateQueryPivotDimensions(
  dimensions: ViewConfigDimension[],
  kpiIds: string[],
): ViewQueryDimensionFieldForMultiTablePivot[] {
  return dimensions.map((d): ViewQueryDimensionFieldForMultiTablePivot => {
    if (d.key === CORE_CONSTANT.KPI_PIVOT_KPI_DIMENSION.key) {
      return {
        name: d.key,
        fixedValues: kpiIds,
      }
    }

    return {
      name: generateDimensionFieldName(d),
      sortedValues: d.sortedValues,
    }
  })
}

export function isInvisibleKpi(kpiId: string, additionalConfig?: ViewConfigAdditionalConfig) {
  const invisibleKpiIds = additionalConfig?.kpiParameter?.invisibleKpiIds ?? []
  // logs.warn(`KPIは非表示です。viewId: ${kpiRef.viewId}`)
  return invisibleKpiIds.includes(kpiId)
}

export function isFilteredKpiGroup(kpiGroupName: string, additionalConfig?: ViewConfigAdditionalConfig) {
  const dimensionFilterLeafs = additionalConfig?.kpiParameter?.dimensionFilterLeafs ?? []
  const kpiGroupDimensionFilterLeaf = dimensionFilterLeafs.find(
    (x) => x.type === 'dimension' && x.dimension.key === CORE_CONSTANT.KPI_PIVOT_KPI_GROUP_DIMENSION().key,
  )
  if (kpiGroupDimensionFilterLeaf === undefined) {
    return false
  }
  const parsedFilterValue = z.string().array().safeParse(kpiGroupDimensionFilterLeaf.filterValue)
  if (!parsedFilterValue.success) {
    return false
  }
  return !parsedFilterValue.data.includes(kpiGroupName)
}
